import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthState } from './auth/store/models/auth-state.interface';
import { Store } from '@ngrx/store';
import { Observable, tap } from 'rxjs';
import { LocalStorageKeys } from './enums/local-storage-keys.enum';
import { environment } from '../environments/environment';
import * as GeneralActions from './+state/general.actions';
import * as GeneralSelectors from './+state/general.selectors';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, map } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { AppRoutesEnum } from './enums/app-routes.enum';
import * as AuthActions from './auth/store/actions/auth.actions';
import * as AuthSelectors from './auth/store/selectors/auth.selector';
import { ModalService } from '@aft/frogo-kit/services/modal.service';

@UntilDestroy()
@Component({
    selector: 'aft-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false,
})
export class AppComponent implements AfterViewInit, OnDestroy {
  public lang$: Observable<string> = this._store.select(GeneralSelectors.langSelector);

  protected readonly environment = environment;

  private _lang: string;
  private _permissionsUpdateTime = 60000;
  private _interval: any;

  constructor(
    private _translate: TranslateService,
    private _store: Store<AuthState>,
    private _router: Router,
    private _modal: ModalService,
  ) {

    this._store.select(AuthSelectors.tokenSelector)
      .pipe(
        filter((token) => token !== null && this._interval === undefined),
      )
      .subscribe(() => {
        this._interval = setInterval(() => {
          this._store.dispatch(AuthActions.updatePermissions());
        }, this._permissionsUpdateTime);
      });

    this._store.select(AuthSelectors.tokenSelector)
      .pipe(
        filter((token) => token === null),
      )
      .subscribe(() => {
        clearInterval(this._interval);
      });

    this._router.events
      .pipe(
        untilDestroyed(this),
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map(e => e.url),
        tap(() => {
          this._modal.hideAll();
        }),
        filter(url => !url.includes(AppRoutesEnum.Login)),
      )
      .subscribe((url) => {
        localStorage.setItem(LocalStorageKeys.LastUrl, url);
      });

    const token = localStorage.getItem(LocalStorageKeys.Token);
    if (token) {
      this._store.dispatch(AuthActions.parseToken({ token }));
    }
  }

  public ngAfterViewInit(): void {
    const lang = localStorage.getItem(LocalStorageKeys.Lang) || environment.defaultLang;

    this.lang$
      .pipe(
        untilDestroyed(this),
        filter(Boolean),
        filter(lang => this._lang !== lang),
      )
      .subscribe(this._setLang.bind(this));

    this._store.dispatch(GeneralActions.setLang({ lang }));
  }

  public ngOnDestroy(): void {
    clearInterval(this._interval);
  }

  private _setLang(lang: string): void {
    this._lang = lang;
    this._translate.setDefaultLang(lang);
    this._translate.use(lang);

    localStorage.setItem(LocalStorageKeys.Lang, lang);
  }
}
